import { apiRequest } from '@/shared/lib/api-client';

export interface DashboardKPIs {
  period: { start: string; end: string };
  tickets_created: number;
  tickets_pending_now: number;
  /** Tickets criados via inbound (cliente abriu) no período. */
  receptive_count: number;
  /** Tickets criados via active-outbound (atendente abriu) no período. */
  active_outbound_count: number;
  /** Total de users com profile='attendant' e status='active' (tenant-level). */
  agents_total: number;
  contacts_total: number;
  contacts_new: number;
  contacts_active: number;
  /** KPI legacy: users com ticket aberto agora. */
  agents_active_now: number;
  /** Enviadas no período por atendente e bot — todos os canais, ou o canal do filtro. */
  messages_sent: number;
  avg_handle_time_seconds: number | null;
  tickets_closed: number;
  avg_first_response_seconds: number | null;
}

export interface DashboardCharts {
  period: { start: string; end: string };
  by_channel: Array<{ id: number; name: string; count: number }>;
  by_closing_reason: Array<{ id: number; name: string; count: number }>;
  by_department: Array<{ id: number; name: string; count: number }>;
  by_day: Array<{ day: string; count: number }>;
  /** 24 entradas (0..23). Sempre preenchido — horas vazias têm count=0. */
  by_hour: Array<{ hour: number; count: number }>;
}

/** Filtros opcionais do dashboard v2. */
export interface DashboardFilters {
  /** ISO datetime. Se omitido, usa `range` ou default (30d). */
  start?: string;
  /** ISO datetime. Se omitido, usa NOW. */
  end?: string;
  /** Atalho: 7/30/90 dias. Ignorado se start/end estiverem setados. */
  range?: '7d' | '30d' | '90d';
  user_id?: number;
  department_id?: number;
  whatsapp_number_id?: number;
}

/**
 * Opções do PEDIDO, separadas dos filtros de propósito: `DashboardFilters` é a
 * chave do cache do TanStack, e enfiar `fresh` ali criaria uma entrada nova a
 * cada clique em "Atualizar".
 */
export interface OpcoesDoPedido {
  /** Clique em "Atualizar": a API ignora o número guardado e calcula na hora. */
  fresh?: boolean;
}

function toQuery(filters: DashboardFilters, opcoes: OpcoesDoPedido = {}): string {
  const params = new URLSearchParams();
  if (filters.start) params.set('start', filters.start);
  if (filters.end) params.set('end', filters.end);
  if (filters.range && !filters.start && !filters.end) params.set('range', filters.range);
  if (filters.user_id !== undefined) params.set('user_id', String(filters.user_id));
  if (filters.department_id !== undefined)
    params.set('department_id', String(filters.department_id));
  if (filters.whatsapp_number_id !== undefined) {
    params.set('whatsapp_number_id', String(filters.whatsapp_number_id));
  }
  if (opcoes.fresh) params.set('fresh', '1');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const getKpis = (
  filters: DashboardFilters,
  opcoes?: OpcoesDoPedido,
): Promise<DashboardKPIs> =>
  apiRequest<DashboardKPIs>(`/dashboard/kpis${toQuery(filters, opcoes)}`);

export const getCharts = (
  filters: DashboardFilters,
  opcoes?: OpcoesDoPedido,
): Promise<DashboardCharts> =>
  apiRequest<DashboardCharts>(`/dashboard/charts${toQuery(filters, opcoes)}`);

/** Pesquisa de Satisfação — média, distribuição 1-5 e média por atendente. */
export interface DashboardRatings {
  total: number;
  average: number | null;
  distribution: Record<string, number>;
  by_agent: Array<{ user_id: number | null; name: string; average: number; count: number }>;
}

export const getRatings = (
  filters: DashboardFilters,
  opcoes?: OpcoesDoPedido,
): Promise<DashboardRatings> =>
  apiRequest<DashboardRatings>(`/dashboard/ratings${toQuery(filters, opcoes)}`);
